import { PageStoreConstructorArgs } from "@/store/types";
import { AppStore, PublicAppStore } from "@/store";
import { makeAutoObservable, reaction } from "mobx";
import { QuickSearchModalStore } from "@/store/pages/QuickSearchModalStore/QuickSearchModalStore";
import { SettingsEmailPreferencesPageStore } from "@/store/pages/SettingsEmailPreferencesPageStore/SettingsEmailPreferencesPageStore";
import { SettingsAccountManagementPageStore } from "@/store/pages/SettingsAccountManagementPageStore/SettingsAccountManagementPageStore";
import { SettingsSubscriptionManagementPageStore } from "@/store/pages/SettingsSubscriptionManagementPageStore";
import { TabPageStoreManager } from "@/store/TabPageStoreManager";
import { Tab } from "@/store/navigation/types";
import { To } from "react-router";
import { ClassicChatHistoryStore } from "./pages/ClassicChatHistoryStore";
import { FloatingAgentPageStore } from "./pages/FloatingAgentPageStore/FloatingAgentPageStore";

export type PageStoreType =
  | "notesListPage"
  | "notesViewPage"
  | "collectionsListPage"
  | "collectionsViewPage"
  | "searchPage"
  | "agentSearchPage"
  | "templatesListPage"
  | "templatesViewPage"
  | "trashPage"
  | "sessionsListPage"
  | "sessionsViewPage"
  | "audioRecordingsListPage";

export class PageStore {
  private tabPageStoreManager: TabPageStoreManager;
  settingsAccountManagementPage: SettingsAccountManagementPageStore;
  settingsEmailPreferencesPage: SettingsEmailPreferencesPageStore;
  settingsSubscriptionManagementPage: SettingsSubscriptionManagementPageStore;
  quickSearchModal: QuickSearchModalStore;
  publicAppStore: PublicAppStore;
  store: AppStore;
  classicChatHistory: ClassicChatHistoryStore;
  floatingAgentPage: FloatingAgentPageStore;

  constructor({ publicAppStore, store, api, pusher }: PageStoreConstructorArgs) {
    const injectedDeps = { store, api, pusher };

    this.publicAppStore = publicAppStore;
    this.store = store;

    // Initialize tab-specific store manager
    this.tabPageStoreManager = new TabPageStoreManager(injectedDeps);

    // Initialize global stores (not tab-specific)
    this.settingsAccountManagementPage = new SettingsAccountManagementPageStore(injectedDeps);
    this.settingsEmailPreferencesPage = new SettingsEmailPreferencesPageStore(injectedDeps);
    this.settingsSubscriptionManagementPage = new SettingsSubscriptionManagementPageStore(
      injectedDeps
    );
    this.quickSearchModal = new QuickSearchModalStore(injectedDeps);
    this.classicChatHistory = new ClassicChatHistoryStore(injectedDeps);
    this.floatingAgentPage = new FloatingAgentPageStore(injectedDeps);

    makeAutoObservable(this);

    reaction(
      () => Array.from(this.store.navigation.tabs.tabsMap.keys()),
      navTabIds => {
        const pageTabIds = this.getAllTabIds();
        for (const tabId of pageTabIds) {
          if (!navTabIds.includes(tabId)) this.removeTabStores(tabId);
        }
      }
    );
  }

  getPageStore(activeTab: Tab | null, activeTabPath: To, pageType: PageStoreType) {
    return this.tabPageStoreManager.getPageStore(activeTab, activeTabPath, pageType);
  }

  removeTabStores(tabId: string) {
    this.tabPageStoreManager.removeTabStores(tabId);
  }

  hasPageStore(tabId: string, pageType: PageStoreType): boolean {
    return this.tabPageStoreManager.hasPageStore(tabId, pageType);
  }

  getAllTabIds(): string[] {
    return this.tabPageStoreManager.getAllTabIds();
  }

  useInitializePageStoreEffects = () => {
    this.store.templates.initialize();
  };
}
